import { lazy, Suspense, useContext, useEffect, useState } from 'react';
import './App.css';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import { decryptAES128CTR } from './Components/Common/Decrypt';
import Navbar from './Components/Common/Navbar Section/Navbar';
import SideBar from './Components/Common/Sidebar Section/SideBar';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import ScrollToTop from './Components/Common/ScrollToTop';
import { useDispatch, useSelector } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import AuthContext from './Components/Common/authContext';
import { addSelectedCategory, deleteItem } from './Components/redux/productSlice';
import CartPopupModal from './Components/Common/Cart Popup/CartPopup';
import CartSidebar from './Components/Common/Cart SideBar/CartSidebar';
import FavoritePopup from './Components/Common/Favorite Popup/FavoritePopup';
import AddCartPopup from './Components/Common/Add To Cart Popup/AddCartPopup';
import MainFooter from './Components/Common/Main Footer/MainFooter';
import ErrorPage from './Components/Common/ErrorPage';
import NorthIcon from '@mui/icons-material/North';
import { gapi } from 'gapi-script';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { buildOrganizationJsonLd, buildWebSiteJsonLd } from './Components/Common/Seo';

// ---------------------------------------------------------------------------
// Route-level code splitting — each page's JS loads only when first visited.
// This breaks the 12 MB monolithic bundle into small per-route chunks so
// mobile browsers never have to parse all of it on the home page.
// ---------------------------------------------------------------------------
const MainHomePage         = lazy(() => import('./Components/Main Home Page/MainHomePage'));
const BookDetailsPage      = lazy(() => import('./Components/Book Details Page/BookDetailsPage'));
const BooksPage            = lazy(() => import('./Components/Books page/BooksPage'));
const Cart                 = lazy(() => import('./Components/Cart Page/Cart'));
const Favorite             = lazy(() => import('./Components/Favorite Page/Favorite'));
const Compare              = lazy(() => import('./Components/Compare Page/Compare'));
const ContactUsPage        = lazy(() => import('./Components/Contact us Page/ContactPage'));
const OrderTracking        = lazy(() => import('./Components/Order Tracking/OrderTracking'));
const FollowOrder          = lazy(() => import('./Components/Order Tracking/Follow Order Page/FollowOrder'));
const Review               = lazy(() => import('./Components/Order Tracking/Review page/Review'));
const Event                = lazy(() => import('./Components/Event Page/Event'));
const EventDetails         = lazy(() => import('./Components/Event Page/Event Details Page/EventDetails'));
const CheckOut             = lazy(() => import('./Components/CheckOut Page/CheckOut'));
const SuccessPage          = lazy(() => import('./Components/CheckOut Page/SuccessPage'));
const CollectionsPage      = lazy(() => import('./Components/Collection Pages/Collections Page/Collections'));
const CollectionDetailsPage= lazy(() => import('./Components/Collection Pages/CollectionDetails Page/CollectionDetails'));
const Collaborators        = lazy(() => import('./Components/Collaborators/Collaborators Page/Collaborators'));
const CollaboratorDetails  = lazy(() => import('./Components/Collaborators/Collaborator Details Page/CollaboratorDetails'));
const Mentions             = lazy(() => import('./Components/Mentions Légales/Mentions'));
const Login                = lazy(() => import('./Components/Auth Pages/Login Page/Login'));
const Register             = lazy(() => import('./Components/Auth Pages/Register Page/Register'));
const ForgotPassword       = lazy(() => import('./Components/Auth Pages/Forgot Password/ForgotPassword'));
const NewPassword          = lazy(() => import('./Components/Auth Pages/New Password/NewPassword'));
const VerifyPage           = lazy(() => import('./Components/Auth Pages/Verify Page/VerifyPage'));
const ResendVerifyPage     = lazy(() => import('./Components/Auth Pages/Verify Page/ResendVerifyPage'));
const Propos               = lazy(() => import('./Components/Librairie Page/Propos'));
const CGV                  = lazy(() => import('./Components/Legal Pages/CGV'));
const MentionsLegales      = lazy(() => import('./Components/Legal Pages/MentionsLegales'));
const PolitiqueConfidentialite = lazy(() => import('./Components/Legal Pages/PolitiqueConfidentialite'));
const Livraison             = lazy(() => import('./Components/Legal Pages/Livraison'));
const RetoursRemboursements = lazy(() => import('./Components/Legal Pages/RetoursRemboursements'));
const AccountPage          = lazy(() => import('./Components/Account Page/AccountPage'));
const OrderReturns         = lazy(() => import('./Components/Account Page/Order Returns/OrderReturns'));
const AddReturn            = lazy(() => import('./Components/Account Page/Order Returns/Add Return/AddReturn'));
const Responses            = lazy(() => import('./Components/Account Page/Order Returns/Responses Page/Responses'));
const AddBlog              = lazy(() => import('./Components/Blogs Page/Add Blog/AddBlog'));
const BlogDetails          = lazy(() => import('./Components/Blogs Page/Blog Details/BlogDetails'));
const BlogPage             = lazy(() => import('./Components/Blogs Page/Blogs Page/BlogPage'));
const EditBlog             = lazy(() => import('./Components/Blogs Page/Edit Blog/EditBlog'));
const AffiliateProgramDetails = lazy(() => import('./Components/Account Page/Account Profile Page/Affiliate Program/AffiliateProgramDetails'));
const TrackOrder           = lazy(() => import('./Components/Order Tracking/Track Order/TrackOrder'));
const CategoriesPage       = lazy(() => import('./Components/Categories Page/CategoriesPage'));
const SubCategoriesPage    = lazy(() => import('./Components/SubCategories Page/SubCategoriesPage'));
const SubSubCategoriesPage = lazy(() => import('./Components/SubSubCategory Page/SubSubCategoriesPage'));
const BestSellersPage      = lazy(() => import('./Components/Best Sellers Page/BestSellersPage'));
const NouveautesPage       = lazy(() => import('./Components/Nouveautes Page/NouveautesPage'));
const Publishers           = lazy(() => import('./Components/Collaborators/Publishers Page/Publishers'));
const PublisherDetails     = lazy(() => import('./Components/Collaborators/Publisher Details Page/PublisherDetails'));
const LibrairiePublishers  = lazy(() => import('./Components/Collaborators/Librairie Publishers Page/LibrairiePublishers'));

const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

const NOINDEX_PATHS = [
  '/cart', '/checkout', '/account', '/wishlist', '/compare',
  '/login', '/register', '/forget-password', '/reset-password',
  '/verify-email', '/resend-verify-email', '/ordertracking', '/followorder/',
  '/review/', '/add_refund_return/', '/refund_return', '/order-success',
  '/edit-blog/', '/add-blog', '/affiliate/',
];

// Non-production hosts (dev/recette/staging) must never be indexed by Google.
// Safe-by-default: only forces noindex when the hostname clearly looks like a
// non-prod environment; an unrecognized host is left alone so this can never
// accidentally noindex production. Mirrors the X-Robots-Tag rule in .htaccess.
const NON_PROD_HOST_RE = /(dev|recette|staging|test|localhost|^\d+\.\d+\.\d+\.\d+$)/i;
const isNonProdHost = () =>
  typeof window !== 'undefined' && NON_PROD_HOST_RE.test(window.location.hostname);

const SEO_ROUTE_SECTION_MAP = [
  { test: (pathname) => pathname === '/', sectionId: 27 },
  { test: (pathname) => pathname.startsWith('/products'), sectionId: 30 },
  { test: (pathname) => pathname.startsWith('/bestsellers'), sectionId: 49 },
  { test: (pathname) => pathname.startsWith('/nouveautes'), sectionId: 48 },
  { test: (pathname) => pathname.startsWith('/events') || pathname.startsWith('/eventdetails'), sectionId: 29 },
  { test: (pathname) => pathname.startsWith('/about-us'), sectionId: 28 },
  { test: (pathname) => pathname.startsWith('/contactus'), sectionId: 31 },
  { test: (pathname) => pathname.startsWith('/blogs') || pathname.startsWith('/blogdetails') || pathname.startsWith('/add-blog') || pathname.startsWith('/edit-blog'), sectionId: 8 },
  { test: (pathname) => pathname.startsWith('/collections') || pathname.startsWith('/collection-details'), sectionId: 26 },
  { test: (pathname) => pathname.startsWith('/collaborators') || pathname.startsWith('/collaborator') || pathname.startsWith('/brands'), sectionId: 56 },
  { test: (pathname) => pathname.startsWith('/cart'), sectionId: 11 },
  { test: (pathname) => pathname.startsWith('/checkout') || pathname.startsWith('/order-success'), sectionId: 12 },
  { test: (pathname) => pathname.startsWith('/wishlist'), sectionId: 55 },
  { test: (pathname) => pathname.startsWith('/compare'), sectionId: 14 },
  { test: (pathname) => pathname.startsWith('/account') || pathname.startsWith('/affiliate') || pathname.startsWith('/refund_return') || pathname.startsWith('/add_refund_return'), sectionId: 15 },
  { test: (pathname) => pathname.startsWith('/ordertracking') || pathname.startsWith('/followorder') || pathname.startsWith('/review') || pathname.startsWith('/track-order'), sectionId: 16 },
  { test: (pathname) => pathname.startsWith('/legal-information'), sectionId: 57 },
  { test: (pathname) => pathname.startsWith('/brands'), sectionId: 44 },
  { test: (pathname) => pathname.startsWith('/login') || pathname.startsWith('/register') || pathname.startsWith('/forget-password') || pathname.startsWith('/reset-password') || pathname.startsWith('/verify-email') || pathname.startsWith('/resend-verify-email'), sectionId: 18 },
];

// Owns the PayPalScriptProvider so the rest of the app never blocks on it.
const CheckoutWrapper = () => {
  const authCtx = useContext(AuthContext);
  const currency = useSelector((state) => state.products.selectedCurrency[0].currency);
  const [paypalClientId, setPaypalClientId] = useState(null);

  useEffect(() => {
    const fetchPaypal = async () => {
      const enc = authCtx.societeConfig?.find(
        (item) => item.config_module_nomtech === 'paypal_client_id'
      )?.valeur;
      if (enc) {
        const dec = await decryptAES128CTR(enc);
        setPaypalClientId(dec);
      }
    };
    fetchPaypal();
  }, [authCtx.societeConfig]);

  if (!paypalClientId) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <p style={{ color: '#671929', fontWeight: 600 }}>Chargement du paiement…</p>
      </div>
    );
  }

  const currencyCode = currency === 'eur' ? 'EUR' : 'USD';
  return (
    <PayPalScriptProvider options={{ clientId: paypalClientId, currency: currencyCode, intent: 'capture' }}>
      <CheckOut />
    </PayPalScriptProvider>
  );
};

const CategoryRoute = () => {
  const { subcatname } = useParams();
  return subcatname ? (
    <ScrollToTop><SubCategoriesPage /></ScrollToTop>
  ) : (
    <ScrollToTop><CategoriesPage /></ScrollToTop>
  );
};

// Shown while a lazy route chunk is downloading
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', color: '#671929' }}>
    Chargement…
  </div>
);

function App() {
  const authctx = useContext(AuthContext);
  const location = useLocation();
  const path = location.pathname;
  const [isAuthPages, setisAuthPages] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [cartisOpen, setcartIsOpen] = useState(false);
  const [pageMetaData, setPageMetaData] = useState({});
  const user = useSelector((state) => state.products.userInfo);
  const productData = useSelector((state) => state.products.productData);
  const dispatch = useDispatch();

  const normalizeMetaValue = (value) => (typeof value === 'string' ? value.trim() : '');

  const getSectionIdForPath = (pathname) => {
    const matchedRoute = SEO_ROUTE_SECTION_MAP.find((route) => route.test(pathname));
    return matchedRoute?.sectionId ?? null;
  };

  const toggle = () => { setIsOpen(!isOpen); };

  const scrollToTop = () => { window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const carttoggle = () => {
    if (!cartisOpen) { authctx.fetchfavandcartSettings(); }
    setcartIsOpen(!cartisOpen);
  };

  useEffect(() => {
    function start() {
      gapi.client.init({ clientId: clientId, scope: '' });
    }
    gapi.load('client:auth2', start);
  }, []);

  useEffect(() => {
    if (
      path.startsWith("/login") ||
      path.startsWith("/register") ||
      path.startsWith("/forget-password") ||
      path.startsWith("/reset-password") ||
      path.startsWith("/verification") ||
      path.startsWith("/resend-verify-email") ||
      path.startsWith("/verify-email")) {
      setisAuthPages(true);
    } else { setisAuthPages(false); }
  }, [path]);

  useEffect(() => {
    if (!path.startsWith("/cp")) {
      localStorage.setItem('route', 'all');
      dispatch(addSelectedCategory(null));
    }
  }, [path, dispatch]);

  useEffect(() => {
    if (!Array.isArray(productData) || productData.length === 0) return;
    const invalidItems = productData.filter((item) => {
      if (!item || typeof item !== "object") return true;
      const hasIdentity = Boolean(item.cart_id || item._id || item.id);
      const hasDisplayData = Boolean(item.title || item.name || item.designation || item.image);
      const parsedPrice = Number(item.price_ttc ?? item.price);
      const hasInvalidPrice = !Number.isFinite(parsedPrice);
      return !hasIdentity || !hasDisplayData || hasInvalidPrice;
    });
    if (invalidItems.length === 0) return;
    invalidItems.forEach((item) => {
      dispatch(deleteItem({ ...item, _id: item?._id ?? item?.id }));
    });
  }, [productData, dispatch]);

  useEffect(() => {
    const sectionId = getSectionIdForPath(path);
    if (!sectionId) { setPageMetaData({}); return; }
    let isMounted = true;
    const fetchMetaData = async () => {
      try {
        const response = await axios.get(`${import.meta.env.VITE_TESTING_API}/seo-meta?ecom_type=hanoot&section_id=${sectionId}`);
        if (isMounted) setPageMetaData(response?.data?.data?.[0] || {});
      } catch {
        if (isMounted) setPageMetaData({});
      }
    };
    fetchMetaData();
    return () => { isMounted = false; };
  }, [path]);

  const metaTitle = normalizeMetaValue(pageMetaData?.meta_title);
  const metaDescription = normalizeMetaValue(pageMetaData?.meta_description);
  const keyword = normalizeMetaValue(pageMetaData?.keyword);
  const secondaryKeyword = normalizeMetaValue(pageMetaData?.secondary_keyword);
  const longTrainText = normalizeMetaValue(pageMetaData?.long_train_text);

  const canonicalPath = path.replace(/\/+$/, '') || '/';
  const isNoIndex = NOINDEX_PATHS.some((p) => path.startsWith(p)) || isNonProdHost();
  const isHome = path === '/';
  const orgJsonLd = buildOrganizationJsonLd(authctx.companySettings);
  const websiteJsonLd = buildWebSiteJsonLd();

  return (
    <div className="App">
      <Helmet>
        <html lang="fr" />
        {metaTitle && <title>{metaTitle}</title>}
        {metaDescription && <meta name="description" content={metaDescription} />}
        {keyword && <meta name="keywords" content={keyword} />}
        {secondaryKeyword && <meta name="secondary_keyword" content={secondaryKeyword} />}
        {longTrainText && <meta name="long_train_text" content={longTrainText} />}
        <link rel="canonical" href={`https://ohanoot.fr${canonicalPath}`} />
        <meta property="og:site_name" content="Ohanoot" />
        <meta property="og:locale" content="fr_FR" />
        {metaTitle && <meta property="og:title" content={metaTitle} />}
        {metaDescription && <meta property="og:description" content={metaDescription} />}
        <meta property="og:url" content={`https://ohanoot.fr${canonicalPath}`} />
        {isNoIndex && <meta name="robots" content="noindex,nofollow" />}
        {/* Organization schema site-wide (not just home) — every page should
            let crawlers resolve "who runs this site" the same way. WebSite
            schema (search action, etc.) stays home-only, that one really is
            page-specific. */}
        <script type="application/ld+json">
          {JSON.stringify(isHome ? [orgJsonLd, websiteJsonLd] : orgJsonLd)}
        </script>
      </Helmet>
      {!isAuthPages && <Navbar toggle={toggle} carttoggle={carttoggle} />}
      <SideBar isOpen={isOpen} toggle={toggle} carttoggle={carttoggle} />
      <CartSidebar isOpen={cartisOpen} toggle={carttoggle} />
      <CartPopupModal />
      <AddCartPopup />

      <div className="appLayout">
        <div className="pageContent">
          <div style={{ transition: 'opacity 0.3s ease-in-out' }}>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path='/' element={<ScrollToTop><MainHomePage carttoggle={carttoggle} /></ScrollToTop>} />
                <Route path='/cp/:catname/:subcatname/:subsubcatname/:id' element={<ScrollToTop><SubSubCategoriesPage /></ScrollToTop>} />
                <Route path='/cp/:catname/:subcatname/:subsubcatname/:productname/:id' element={<ScrollToTop><BookDetailsPage carttoggle={carttoggle} /></ScrollToTop>} />
                <Route path='/cp/:catname/:subcatname/:subsubcatname/:productname/:id/:token' element={<ScrollToTop><BookDetailsPage carttoggle={carttoggle} /></ScrollToTop>} />
                <Route path='/products' element={<ScrollToTop><BooksPage carttoggle={carttoggle} /></ScrollToTop>} />
                <Route path='/products/subcategory/:id' element={<ScrollToTop><BooksPage carttoggle={carttoggle} /></ScrollToTop>} />
                <Route path='/cart' element={<ScrollToTop><Cart /></ScrollToTop>} />
                <Route path='/checkout' element={<ScrollToTop><CheckoutWrapper /></ScrollToTop>} />
                <Route path='/wishlist' element={<ScrollToTop><Favorite carttoggle={carttoggle} /></ScrollToTop>} />
                <Route path='/compare' element={<ScrollToTop><Compare /></ScrollToTop>} />
                <Route path='/login' element={<ScrollToTop><Login /></ScrollToTop>} />
                <Route path='/register' element={<ScrollToTop><Register /></ScrollToTop>} />
                <Route path='/forget-password' element={<ScrollToTop><ForgotPassword /></ScrollToTop>} />
                <Route path='/reset-password' element={<ScrollToTop><NewPassword /></ScrollToTop>} />
                <Route path='/verify-email' element={<ScrollToTop><VerifyPage /></ScrollToTop>} />
                <Route path='/resend-verify-email' element={<ScrollToTop><ResendVerifyPage /></ScrollToTop>} />
                <Route path="/events" element={<ScrollToTop><Event /></ScrollToTop>} />
                <Route path="/about-us" element={<ScrollToTop><Propos /></ScrollToTop>} />
                <Route path='/contactus' element={<ScrollToTop><ContactUsPage /></ScrollToTop>} />
                <Route path='/cgv' element={<ScrollToTop><CGV /></ScrollToTop>} />
                <Route path='/mentions-legales' element={<ScrollToTop><MentionsLegales /></ScrollToTop>} />
                <Route path='/politique-de-confidentialite' element={<ScrollToTop><PolitiqueConfidentialite /></ScrollToTop>} />
                <Route path='/livraison' element={<ScrollToTop><Livraison /></ScrollToTop>} />
                <Route path='/retours-remboursements' element={<ScrollToTop><RetoursRemboursements /></ScrollToTop>} />
                <Route path='/ordertracking' element={user ? <ScrollToTop><OrderTracking /></ScrollToTop> : <Navigate to="/login" replace />} />
                <Route path='/ordertracking/:orderId/*' element={<ScrollToTop><OrderTracking /></ScrollToTop>} />
                <Route path='/track-order' element={<ScrollToTop><TrackOrder /></ScrollToTop>} />
                <Route path='/affiliate/:id' element={<ScrollToTop><AffiliateProgramDetails /></ScrollToTop>} />
                <Route path='/followorder/:orderId' element={user ? <ScrollToTop><FollowOrder /></ScrollToTop> : <Navigate to="/login" replace />} />
                <Route path='/review/:orderId' element={<ScrollToTop><Review /></ScrollToTop>} />
                <Route path="/eventdetails/:eventId" element={<ScrollToTop><EventDetails /></ScrollToTop>} />
                <Route path='/account' element={user ? <ScrollToTop><AccountPage /></ScrollToTop> : <Navigate to="/login" replace />} />
                <Route path="/account/:pageId" element={user ? <ScrollToTop><AccountPage /></ScrollToTop> : <Navigate to="/login" replace />} />
                <Route path='/add_refund_return/:id' element={<ScrollToTop><AddReturn /></ScrollToTop>} />
                <Route path='/refund_return' element={user ? <ScrollToTop><OrderReturns /></ScrollToTop> : <Navigate to="/login" replace />} />
                <Route path='/refund_return/:returnId' element={user ? <ScrollToTop><Responses /></ScrollToTop> : <Navigate to="/login" replace />} />
                <Route path='/cp/:catname/:subcatname?/:catId' element={<CategoryRoute />} />
                <Route path='/collections' element={<ScrollToTop><CollectionsPage /></ScrollToTop>} />
                <Route path='/collection-details/:id' element={<ScrollToTop><CollectionDetailsPage /></ScrollToTop>} />
                <Route path='/collaborator/:id/details' element={<ScrollToTop><CollaboratorDetails /></ScrollToTop>} />
                <Route path='/collaborators' element={<ScrollToTop><Collaborators /></ScrollToTop>} />
                <Route path="/brands" element={<ScrollToTop><Publishers /></ScrollToTop>} />
                <Route path="/brands/:id/details" element={<ScrollToTop><PublisherDetails /></ScrollToTop>} />
                <Route path="/publishers" element={<ScrollToTop><LibrairiePublishers /></ScrollToTop>} />
                <Route path='/order-success' element={<ScrollToTop><SuccessPage /></ScrollToTop>} />
                <Route path='/legal-information' element={<ScrollToTop><Mentions /></ScrollToTop>} />
                <Route path='/edit-blog/:blogId' element={<ScrollToTop><EditBlog /></ScrollToTop>} />
                <Route path='/add-blog' element={<ScrollToTop><AddBlog /></ScrollToTop>} />
                <Route path="/blogdetails/:blogId" element={<ScrollToTop><BlogDetails /></ScrollToTop>} />
                <Route path="/blogs" element={<ScrollToTop><BlogPage /></ScrollToTop>} />
                <Route path="/bestsellers" element={<ScrollToTop><BestSellersPage /></ScrollToTop>} />
                <Route path="/nouveautes" element={<ScrollToTop><NouveautesPage /></ScrollToTop>} />
                <Route path="*" element={<ErrorPage />} />
              </Routes>
            </Suspense>
          </div>
          {!isAuthPages && <MainFooter />}
        </div>
      </div>
      <div className="scrollToTopButton" onClick={scrollToTop}>
        <NorthIcon style={{ margin: 'auto', width: '90%', height: '70%' }} />
      </div>
      <FavoritePopup />
      <ToastContainer />
    </div>
  );
}

export default App;
